import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Hls from 'hls.js'
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Loader2,
  RotateCcw,
  AlertCircle,
  RefreshCw
} from 'lucide-react'
import { ContentMetadataText } from './common/ContentMetadataText'

// Queture 응답 타입 (앱마다 다를 수 있음)
export interface QuetureResponse {
  qid?: string
  quetureId?: string
  title?: string | null
  hlsUrl?: string | null
  thumbnailUrl?: string | null
  duration?: number | null
}

// Queture API 인터페이스 (앱에서 구현)
export interface QuetureApiInterface {
  getQuetureInfo(qid: string): Promise<QuetureResponse>
}

export interface QueturePlayerHandle {
  pause: () => void
  play: () => void
  seek: (seconds: number) => void
  /** Immediately emits the currently buffered watch segment through onHeartbeat. */
  flushHeartbeat: () => void
  getCurrentTime: () => number
  /** Max watched progress in seconds (based on maxProgress% × duration). */
  getMaxProgressSec: () => number

  /* ─ externalControls 호스트용 확장 API ─ */
  setPlaybackRate: (rate: number) => void
  isPaused: () => boolean
  getDuration: () => number
  setMuted: (muted: boolean) => void
  setVolume: (volume: number) => void
}

interface QueturePlayerProps {
  qid: string
  thumbnailUrl?: string | null
  title?: string
  /** Host apps opt in; other consumers keep their existing title presentation. */
  renderMetadataMath?: boolean
  onComplete?: () => void
  onProgress?: (progress: number, currentTime: number, duration: number) => void
  autoPlay?: boolean
  quetureApi: QuetureApiInterface
  allowSkip?: boolean
  requiredWatchPercent?: number
  // Direct HLS URL - skips API call when provided (preferred for Learning Session API)
  hlsUrl?: string | null
  // Start time in seconds (for resume from last position)
  startTime?: number
  // Learning Session API integration (optional)
  componentProgressId?: number
  onHeartbeat?: (data: { startTimeSec: number; endTimeSec: number; playbackSpeed: number; wasSeeking: boolean }) => void
  onVideoProgress?: (data: { currentPositionSec: number; progressPercent: number }) => void
  // Callback when forward skip is blocked (allowSkip=false)
  onSkipBlocked?: () => void

  /**
   * true 면 내부 Control Bar / Play Button Overlay / Title Overlay 를 렌더하지 않는다.
   * 호스트가 Handle 로 제어하며 자체 컨트롤 UI 를 상위 레이어에 그린다.
   * 기본 false.
   */
  externalControls?: boolean

  /** 재생 상태(play/pause) 전환 시 호출. */
  onPlayStateChange?: (isPlaying: boolean) => void

  /** 재생 속도 변경 시 호출. */
  onPlaybackRateChange?: (rate: number) => void
}

export const QueturePlayer = forwardRef<QueturePlayerHandle, QueturePlayerProps>(function QueturePlayer({
  qid,
  thumbnailUrl,
  title,
  renderMetadataMath = false,
  onComplete,
  onProgress,
  autoPlay = false,
  quetureApi,
  allowSkip = true,
  requiredWatchPercent = 90,
  hlsUrl: directHlsUrl, // Direct HLS URL from Learning Session API
  startTime = 0, // Start time in seconds (for resume from last position)
  componentProgressId: _componentProgressId, // Reserved for future use
  onHeartbeat,
  onVideoProgress,
  onSkipBlocked,
  externalControls = false,
  onPlayStateChange,
  onPlaybackRateChange,
}: QueturePlayerProps, ref) {
  const { t } = useTranslation('player')
  // Note: _componentProgressId is available for direct API calls if needed in the future
  void _componentProgressId
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const progressBarRef = useRef<HTMLDivElement>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [progress, setProgress] = useState(0)
  const [maxProgress, setMaxProgress] = useState(0) // 최대 진행률 추적
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showControls, setShowControls] = useState(true)
  const [quetureData, setQuetureData] = useState<QuetureResponse | null>(null)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [showSpeedMenu, setShowSpeedMenu] = useState(false)

  // Heartbeat tracking refs
  const lastHeartbeatTimeRef = useRef<number>(0)
  const heartbeatStartTimeRef = useRef<number>(0)
  const wasSeekingRef = useRef<boolean>(false)
  const playbackSpeedRef = useRef<number>(1)

  useImperativeHandle(ref, () => ({
    pause: () => { videoRef.current?.pause() },
    play: () => { videoRef.current?.play().catch(() => {}) },
    seek: (seconds: number) => {
      if (videoRef.current) videoRef.current.currentTime = seconds
    },
    flushHeartbeat: () => {
      const video = videoRef.current
      if (!video || !onHeartbeat || lastHeartbeatTimeRef.current <= 0) return

      onHeartbeat({
        startTimeSec: heartbeatStartTimeRef.current || video.currentTime,
        endTimeSec: video.currentTime,
        playbackSpeed: playbackSpeedRef.current,
        wasSeeking: wasSeekingRef.current,
      })
      lastHeartbeatTimeRef.current = Date.now()
      heartbeatStartTimeRef.current = video.currentTime
      wasSeekingRef.current = false
    },
    getCurrentTime: () => videoRef.current?.currentTime ?? 0,
    getMaxProgressSec: () => {
      // maxProgress is percentage (0-100); convert to seconds of video
      return (maxProgress / 100) * duration
    },
    setPlaybackRate: (rate: number) => {
      if (videoRef.current) {
        videoRef.current.playbackRate = rate
        setPlaybackRate(rate)
        playbackSpeedRef.current = rate
        onPlaybackRateChange?.(rate)
      }
    },
    isPaused: () => videoRef.current?.paused ?? true,
    getDuration: () => videoRef.current?.duration ?? 0,
    setMuted: (muted: boolean) => {
      if (videoRef.current) {
        videoRef.current.muted = muted
        setIsMuted(muted)
      }
    },
    setVolume: (volume: number) => {
      if (videoRef.current) {
        videoRef.current.volume = Math.max(0, Math.min(1, volume))
      }
    },
  }), [maxProgress, duration, onHeartbeat, onPlaybackRateChange])

  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Use direct HLS URL if provided, otherwise fetch from API
  useEffect(() => {
    // If direct HLS URL is provided, use it directly (Learning Session API flow)
    if (directHlsUrl) {
      setQuetureData({
        qid,
        hlsUrl: directHlsUrl,
        thumbnailUrl: thumbnailUrl || null,
        title: title || null
      })
      setIsLoading(false)
      return
    }

    // Fallback: fetch from queture API (legacy flow)
    if (!qid) return

    const fetchQuetureData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const quetureInfo = await quetureApi.getQuetureInfo(qid)

        if (quetureInfo.hlsUrl) {
          setQuetureData(quetureInfo)
        } else {
          setError(t('video.urlNotFound'))
        }
      } catch (err) {
        console.error('Failed to fetch queture data:', err)
        setError(t('video.loadError'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchQuetureData()
  }, [qid, directHlsUrl, thumbnailUrl, title, quetureApi])

  // Initialize HLS player
  useEffect(() => {
    const video = videoRef.current
    if (!video || !quetureData?.hlsUrl) return

    const initPlayer = () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }

      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          xhrSetup: (xhr) => {
            xhr.withCredentials = false
          }
        })

        hls.loadSource(quetureData.hlsUrl!)
        hls.attachMedia(video)

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false)
          // Set start time if provided (resume from last position)
          if (startTime > 0 && video.duration > 0) {
            video.currentTime = Math.min(startTime, video.duration - 1)
          }
          if (autoPlay) {
            video.play().catch(console.error)
          }
        })

        hls.on(Hls.Events.ERROR, (_, data) => {
          console.error('HLS error:', data.type, data.details)

          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                setError(t('video.networkError'))
                setTimeout(() => {
                  hls.startLoad()
                  setError(null)
                }, 1000)
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError()
                break
              default:
                setError(t('video.cannotPlay'))
                hls.destroy()
                break
            }
          }
        })

        hlsRef.current = hls
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS support
        video.src = quetureData.hlsUrl!
        video.addEventListener('loadedmetadata', () => {
          setIsLoading(false)
          // Set start time if provided (resume from last position)
          if (startTime > 0 && video.duration > 0) {
            video.currentTime = Math.min(startTime, video.duration - 1)
          }
          if (autoPlay) {
            video.play().catch(console.error)
          }
        })
      } else {
        setError(t('video.hlsNotSupported'))
      }
    }

    initPlayer()

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy()
        hlsRef.current = null
      }
    }
  }, [quetureData?.hlsUrl, autoPlay])

  // Track if start time has been set (to avoid setting it multiple times)
  const startTimeSetRef = useRef<boolean>(false)

  // Video event listeners
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Reset startTimeSet flag when component re-mounts or video changes
    startTimeSetRef.current = false

    const handleTimeUpdate = () => {
      const currentProgress = (video.currentTime / video.duration) * 100 || 0
      setCurrentTime(video.currentTime)
      setProgress(currentProgress)
      setMaxProgress((prev) => Math.max(prev, currentProgress))

      // Legacy onProgress callback (backward compatibility)
      onProgress?.(currentProgress, video.currentTime, video.duration)

      // New onVideoProgress callback for Learning Session API
      if (onVideoProgress) {
        onVideoProgress({
          currentPositionSec: video.currentTime,
          progressPercent: currentProgress
        })
      }

      // Heartbeat tracking (5 second interval)
      if (onHeartbeat && !video.paused) {
        const now = Date.now()
        const timeSinceLastHeartbeat = (now - lastHeartbeatTimeRef.current) / 1000

        if (timeSinceLastHeartbeat >= 5) {
          const startTime = heartbeatStartTimeRef.current || video.currentTime
          const endTime = video.currentTime

          onHeartbeat({
            startTimeSec: startTime,
            endTimeSec: endTime,
            playbackSpeed: playbackSpeedRef.current,
            wasSeeking: wasSeekingRef.current
          })

          // Reset for next heartbeat
          lastHeartbeatTimeRef.current = now
          heartbeatStartTimeRef.current = endTime
          wasSeekingRef.current = false
        }
      }
    }

    const handleDurationChange = () => {
      setDuration(video.duration)
    }

    const handleLoadedMetadata = () => {
      // Set start time once when metadata is loaded (resume from last position)
      if (startTime > 0 && !startTimeSetRef.current && video.duration > 0) {
        video.currentTime = Math.min(startTime, video.duration - 1)
        startTimeSetRef.current = true
        console.debug(`[QueturePlayer] Set start time to ${startTime}s (duration: ${video.duration}s)`)
      }
    }

    const handlePlay = () => {
      setIsPlaying(true)
      onPlayStateChange?.(true)
      // Initialize heartbeat tracking on play
      if (onHeartbeat) {
        const now = Date.now()
        lastHeartbeatTimeRef.current = now
        heartbeatStartTimeRef.current = video.currentTime
      }
    }

    const handlePause = () => {
      setIsPlaying(false)
      onPlayStateChange?.(false)
      // Send final heartbeat on pause
      if (onHeartbeat && lastHeartbeatTimeRef.current > 0) {
        const startTime = heartbeatStartTimeRef.current || video.currentTime
        const endTime = video.currentTime

        onHeartbeat({
          startTimeSec: startTime,
          endTimeSec: endTime,
          playbackSpeed: playbackSpeedRef.current,
          wasSeeking: wasSeekingRef.current
        })

        // Reset tracking
        wasSeekingRef.current = false
      }
    }
    const handleEnded = () => {
      setIsPlaying(false)
      if (maxProgress >= requiredWatchPercent) {
        onComplete?.()
      }
    }

    const handleSeeking = () => {
      wasSeekingRef.current = true
    }

    const handleSeeked = () => {
      // Reset heartbeat tracking after seeking
      if (onHeartbeat) {
        heartbeatStartTimeRef.current = video.currentTime
        lastHeartbeatTimeRef.current = Date.now()
      }
    }

    const handleRateChange = () => {
      playbackSpeedRef.current = video.playbackRate
    }

    const handleWaiting = () => setIsLoading(true)
    const handleCanPlay = () => setIsLoading(false)
    const handleError = (e: Event) => {
      console.error('Video error:', e)
      setError(t('video.cannotPlay'))
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    video.addEventListener('durationchange', handleDurationChange)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handleEnded)
    video.addEventListener('seeking', handleSeeking)
    video.addEventListener('seeked', handleSeeked)
    video.addEventListener('ratechange', handleRateChange)
    video.addEventListener('waiting', handleWaiting)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('error', handleError)

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
      video.removeEventListener('durationchange', handleDurationChange)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('seeking', handleSeeking)
      video.removeEventListener('seeked', handleSeeked)
      video.removeEventListener('ratechange', handleRateChange)
      video.removeEventListener('waiting', handleWaiting)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('error', handleError)
    }
  }, [onComplete, onProgress, onVideoProgress, onHeartbeat, onPlayStateChange, maxProgress, requiredWatchPercent, startTime])

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Auto-hide controls
  const resetControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
    setShowControls(true)
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false)
      }, 3000)
    }
  }

  const formatTime = (time: number) => {
    if (!time || isNaN(time)) return '00:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return

    if (isPlaying) {
      video.pause()
    } else {
      video.play().catch(console.error)
    }
  }

  const toggleMute = () => {
    const video = videoRef.current
    if (!video) return

    video.muted = !video.muted
    setIsMuted(!isMuted)
  }

  const toggleFullscreen = () => {
    const container = containerRef.current
    if (!container) return

    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(console.error)
    } else {
      document.exitFullscreen().catch(console.error)
    }
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current
    const progressBar = progressBarRef.current
    if (!video || !progressBar) return

    // Skip 불가 시 앞으로만 이동 제한
    if (!allowSkip) {
      const rect = progressBar.getBoundingClientRect()
      const pos = (e.clientX - rect.left) / rect.width
      const targetTime = pos * video.duration
      // 현재 시청한 최대 위치까지만 이동 허용
      if (targetTime > (maxProgress / 100) * video.duration) {
        onSkipBlocked?.()
        return
      }
    }

    const rect = progressBar.getBoundingClientRect()
    const pos = (e.clientX - rect.left) / rect.width
    video.currentTime = pos * video.duration
  }

  const replay = () => {
    const video = videoRef.current
    if (!video) return

    video.currentTime = 0
    video.play().catch(console.error)
  }

  const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

  const changePlaybackRate = (rate: number) => {
    const video = videoRef.current
    if (!video) return

    video.playbackRate = rate
    setPlaybackRate(rate)
    playbackSpeedRef.current = rate
    setShowSpeedMenu(false)
    onPlaybackRateChange?.(rate)
  }

  const handleRetry = () => {
    setError(null)
    setIsLoading(true)

    // If direct HLS URL was provided, just re-use it
    if (directHlsUrl) {
      setQuetureData({
        qid,
        hlsUrl: directHlsUrl,
        thumbnailUrl: thumbnailUrl || null,
        title: title || null
      })
      setIsLoading(false)
      return
    }

    quetureApi
      .getQuetureInfo(qid)
      .then((quetureInfo) => {
        if (quetureInfo.hlsUrl) {
          setQuetureData(quetureInfo)
        } else {
          setError(t('video.urlNotFound'))
        }
      })
      .catch((err) => {
        console.error('Retry failed:', err)
        setError(t('video.loadError'))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const displayThumbnail = quetureData?.thumbnailUrl || thumbnailUrl
  const displayTitle = quetureData?.title || title

  // qid가 없을 때 처리
  if (!qid || qid.trim() === '') {
    return (
      <div className="flex justify-center items-center bg-linear-to-br from-gray-900 to-gray-800 rounded-xl aspect-video">
        <div className="p-6 text-center">
          <AlertCircle className="mx-auto mb-3 w-12 h-12 text-yellow-400" />
          <p className="mb-2 text-white/80">{t('video.noIdProvided')}</p>
          <p className="text-sm text-white/50">{t('video.cannotLoadInfo')}</p>
        </div>
      </div>
    )
  }

  if (error && !quetureData?.hlsUrl) {
    return (
      <div className="flex justify-center items-center bg-linear-to-br from-gray-900 to-gray-800 rounded-xl aspect-video">
        <div className="p-6 text-center">
          <AlertCircle className="mx-auto mb-3 w-12 h-12 text-red-400" />
          <p className="mb-2 text-white/80">{error}</p>
          <button
            onClick={handleRetry}
            className="mt-4 inline-flex gap-2 items-center px-4 py-2 text-white rounded-lg transition-colors bg-white/10 hover:bg-white/20"
          >
            <RefreshCw className="w-4 h-4" />
            {t('video.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="overflow-hidden relative bg-black rounded-xl aspect-video group"
      onMouseMove={resetControlsTimeout}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        className="w-full h-full"
        poster={displayThumbnail || undefined}
        playsInline
        onClick={togglePlay}
        controlsList="nodownload"
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="flex absolute inset-0 justify-center items-center bg-black/50">
          <Loader2 className="w-12 h-12 text-white animate-spin" />
        </div>
      )}

      {/* Play Button Overlay (when paused) */}
      {!externalControls && !isPlaying && !isLoading && quetureData?.hlsUrl && (
        <div
          className="flex absolute inset-0 justify-center items-center cursor-pointer bg-black/30"
          onClick={togglePlay}
        >
          <div className="flex justify-center items-center w-20 h-20 rounded-full backdrop-blur-xs transition-colors bg-white/20 hover:bg-white/30">
            <Play className="ml-1 w-10 h-10 text-white" />
          </div>
        </div>
      )}

      {/* Controls */}
      {!externalControls && (
      <div
        className={`absolute bottom-0 left-0 right-0 z-20 bg-linear-to-t from-black/90 via-black/70 to-transparent p-4 transition-opacity duration-300 ${
          showControls || !isPlaying ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Progress Bar */}
        <div
          ref={progressBarRef}
          className="mb-3 cursor-pointer group/progress"
          onClick={handleProgressClick}
        >
          <div className="w-full h-2 rounded-full border shadow-lg backdrop-blur-xs bg-white/20 border-white/10">
            <div
              className="relative h-full bg-indigo-500 rounded-full shadow-md"
              style={{ width: `${Math.max(progress, 0)}%` }}
            >
              <div className="absolute right-0 top-1/2 w-4 h-4 bg-white rounded-full border-2 border-indigo-500 shadow-lg opacity-0 transition-opacity -translate-y-1/2 group-hover/progress:opacity-100" />
            </div>
          </div>
        </div>

        {/* Control Buttons */}
        <div className="flex justify-between items-center">
          <div className="flex gap-3 items-center">
            {/* Play/Pause */}
            <button
              onClick={togglePlay}
              className="p-2 text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>

            {/* Replay */}
            <button
              onClick={replay}
              className="p-2 text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            {/* Volume */}
            <button
              onClick={toggleMute}
              className="p-2 text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>

            {/* Time Display */}
            <span className="px-2 py-1 text-sm font-medium text-white rounded-xs shadow-md backdrop-blur-xs bg-black/50">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="flex gap-3 items-center">
            {/* Playback Speed */}
            <div className="relative">
              <button
                onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                className="px-2 py-1.5 text-sm font-medium text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl min-w-[52px]"
              >
                {playbackRate === 1 ? '1x' : `${playbackRate}x`}
              </button>
              {showSpeedMenu && (
                <div className="absolute right-0 bottom-full mb-2 py-1 bg-black/90 backdrop-blur-xs rounded-lg shadow-xl border border-white/10 min-w-[80px]">
                  {PLAYBACK_RATES.map((rate) => (
                    <button
                      key={rate}
                      onClick={() => changePlaybackRate(rate)}
                      className={`w-full px-3 py-1.5 text-sm text-left transition-colors ${
                        playbackRate === rate
                          ? 'text-indigo-400 bg-white/10'
                          : 'text-white hover:bg-white/10'
                      }`}
                    >
                      {rate === 1 ? t('video.normal') : `${rate}x`}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-2 text-white rounded-lg shadow-lg backdrop-blur-xs transition-all bg-black/50 hover:bg-black/70 hover:shadow-xl"
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Title Overlay */}
      {!externalControls && displayTitle && showControls && (
        <div className="absolute top-0 right-0 left-0 p-4 bg-linear-to-b to-transparent from-black/60">
          <h3 className="font-medium text-white truncate">
            {renderMetadataMath ? <ContentMetadataText text={displayTitle} /> : displayTitle}
          </h3>
        </div>
      )}
    </div>
  )
})

export default QueturePlayer
